import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useLocation, useParams } from 'react-router-dom'
import config from '../../config'
import { useStoreContext } from '../../contexts/store-context'
import { SessionBrief } from '../../types/SessionBrief'
import { remove, get, mergePatch, post } from '../../utils/http'
import SessionInfo from '../SessionInfo/SessionInfo'
import { useNavigate } from 'react-router-dom'
import SpinnerIcon from '../Icons/SpinnerIcon'
import NavigateBack from '../NavigateBack/NavigateBack'
import './CustomerInfo.scss'
import SessionListItem from '../SessionListItem/SessionListItem'
import { IcaDialog } from '../IcaElements/IcaDialog'
import { IcaHeader } from '../IcaElements/IcaHeader'
import RefreshIcon from '../Icons/RefreshIcon'
import { useDeviceContext } from '../../contexts/device-context'
import { useHistoryContext } from '../../contexts/history-context'
import CustomerCard from './CustomerCard'
import CustomerBannedCard from './CustomerBannedCard'
import CustomerFlaggedCard from './CustomerFlaggedCard'
import { SortingChangedEvent } from '../Picklist/picklistHelpers'
import { IcaSortingDropDown } from '../IcaElements/IcaSortingDropDown'
import { sortByDate } from '../../utils/sortUtils'
import { getCountryCode } from '../../utils/userUtils'
import CustomerFilterCard from './CustomerFilterCard'
import { useFilterContext } from '../../contexts/filter-context'
import SearchPlaceholder from '../SearchPlaceholder/SearchPlaceholder'

export interface Customer {
  id: string
  countryCode: string
  acr: string
  customerBan: CustomerBan | null
  customerFlag: CustomerFlag | null
  sessions: Array<SessionBrief>
  latestActivity?: string
}

export interface CustomerBan {
  customerId: string
  countryCode: string
  acr: string
  updatedById: string
  updatedByEmail: string
  updatedByStoreId: string
  updatedByStoreName: string
  updatedByMainStoreName: string
  createdAt: string
  updatedAt: string
  customerHasNonpayment: boolean
  customerHasMismanagedWeighedItems: boolean
  customerHasLetOthersIntoStore: boolean
  customerHasVandalized: boolean
}

export interface CustomerFlag {
  customerId: string
  countryCode: string
  acr: string
  updatedById: string
  updatedByEmail: string
  createdAt: string
  updatedAt: string
}

interface UpdateCustomerBanRequest {
  customerHasNonpayment: boolean
  customerHasMismanagedWeighedItems: boolean
  customerHasLetOthersIntoStore: boolean
  customerHasVandalized: boolean
}

export default function CustomerInfo() {
  const [customer, setCustomer] = useState<Customer | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const { id, countryCode, sessionId } = useParams()
  const location = useLocation()
  const acr: string = new URLSearchParams(location.search).get('acr') ?? ''
  const { storeState } = useStoreContext()
  const { deviceState } = useDeviceContext()
  const { filterState } = useFilterContext()
  const { historyReducer } = useHistoryContext()
  const [banError, setBanError] = useState(false)
  const [flagError, setFlagError] = useState(false)
  const navigate = useNavigate()
  const backUrl = useRef(location?.state?.previousUrl ?? null)
  const [isSavingBanReasons, setIsSavingBanReasons] = useState(false)
  const [sortingOptions, setSortingOptions] = useState([
    { id: 'start-asc', value: 'Starttid stigande', selected: false },
    { id: 'start-desc', value: 'Starttid fallande', selected: false },
    { id: 'end-asc', value: 'Sluttid stigande', selected: false },
    { id: 'end-desc', value: 'Sluttid fallande', selected: true },
  ])

  const sortedCustomerInfo = useMemo(() => {
    const selectedSortingOption = sortingOptions.find(option => option.selected)?.id
    if (selectedSortingOption) {
      switch (selectedSortingOption) {
        case 'start-asc':
          return { ...customer, sessions: customer ? sortByDate(customer.sessions, session => new Date(session.firstActivity), 'asc') : [] }
        case 'start-desc':
          return { ...customer, sessions: customer ? sortByDate(customer.sessions, session => new Date(session.firstActivity), 'desc') : [] }
        case 'end-asc':
          return { ...customer, sessions: customer ? sortByDate(customer.sessions, session => new Date(session.latestActivity), 'asc') : [] }
        case 'end-desc':
          return { ...customer, sessions: customer ? sortByDate(customer.sessions, session => new Date(session.latestActivity), 'desc') : [] }
        default:
          return customer
      }
    }
  }, [customer, sortingOptions])

  const fetchCustomerInfo = useCallback(async () => {
    setLoading(true)
    setCustomer(null)
    setError(false)

    try {
      const customer = await get<Customer>(`${config.STORE_API_BASE_URL}/v2/monitor/customer/${storeState.selectedStore?.id}/${id}/${countryCode}?flaggedSessions=${filterState.filter.flagged}`)
      if (customer.sessions.length > 0) {
        customer.latestActivity = customer.sessions.reduce((latest, current) => {
          const latestDate = new Date(latest.latestActivity).getTime()
          const currentDate = new Date(current.latestActivity).getTime()
          return currentDate > latestDate ? current : latest
        }).latestActivity
      }
      setCustomer(customer)
    } catch {
      setError(true)
    } finally {
      setLoading(false)
    }
  }, [id, countryCode, storeState.selectedStore?.id, filterState.filter])

  useEffect(() => {
    if (storeState.selectedStore?.id) {
      fetchCustomerInfo()
    }
  }, [storeState.selectedStore?.id, fetchCustomerInfo])

  useEffect(() => {
    if (customer && sessionId && !customer.sessions.find(session => session.id === sessionId)) {
      navigate(`/customers/${id}/${countryCode}${location.search}`)
    }
  }, [customer])

  const onModalClose = () => {
    navigate(`/customers/${id}/${countryCode}${location.search}`)
  }

  const toggleBanCustomer = async (ban: boolean) => {
    if (customer === null) {
      return
    }

    setBanError(false)
    const newCustomer: Customer = { ...customer }

    try {
      if (ban) {
        const body = { StoreId: storeState.selectedStore?.id, CustomerId: id, IdentifierType: 'CipheredSsn', CountryCode: getCountryCode(countryCode, acr), Acr: acr }
        newCustomer.customerBan = await post(`${config.UNMANNED_API_BASE_URL}/v1/unmanned/customer-bans`, body)
      } else { // Unban
        await remove(`${config.UNMANNED_API_BASE_URL}/v1/unmanned/customer-bans/${id}/${countryCode}?identifierType=CipheredSsn`)
        newCustomer.customerBan = null
      }
      setCustomer(newCustomer)
    } catch {
      setBanError(true)
    }
  }

  const toggleFlagCustomer = async (flag: boolean) => {
    if (customer === null) {
      return
    }

    setFlagError(false)
    const newCustomer: Customer = { ...customer }

    try {
      if (flag) {
        const body = { CustomerId: id, CountryCode: getCountryCode(countryCode, acr), Acr: acr }
        newCustomer.customerFlag = await post(`${config.UNMANNED_API_BASE_URL}/v1/unmanned/customer-flags/${storeState.selectedStore?.id}`, body)
      } else { // Unflag
        await remove(`${config.UNMANNED_API_BASE_URL}/v1/unmanned/customer-flags/${storeState.selectedStore?.id}/${id}/${countryCode}`)
        newCustomer.customerFlag = null
      }
      
      newCustomer.sessions = newCustomer.sessions.map(session => ({
        ...session,
        isCustomerFlagged: flag,
      }))

      setCustomer(newCustomer)
    } catch {
      setFlagError(true)
    }
  }

  const handleBanReasonChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const body: Partial<UpdateCustomerBanRequest> = {
      [event.target.name]: event.target.checked,
    }
    await updateCustomerBan(body)
  }

  const handleBanReasonsReset = async () => {
    const body: UpdateCustomerBanRequest = {
      customerHasNonpayment: false,
      customerHasMismanagedWeighedItems: false,
      customerHasLetOthersIntoStore: false,
      customerHasVandalized: false,
    } as UpdateCustomerBanRequest
    await updateCustomerBan(body)
  }

  const updateCustomerBan = async (body: Partial<UpdateCustomerBanRequest>) => {
    if (customer === null) {
      return
    }

    setIsSavingBanReasons(true)
    setBanError(false)

    try {
      const customerBan: CustomerBan = await mergePatch(`${config.UNMANNED_API_BASE_URL}/v1/unmanned/customer-bans/${id}/${countryCode}?storeId=${storeState.selectedStore?.id}&identifierType=CipheredSsn`, body)
      const newCustomer: Customer = { ...customer }
      newCustomer.customerBan = customerBan
      setCustomer(newCustomer)
    } catch {
      setBanError(true)
    }
    finally {
      setIsSavingBanReasons(false)
    }
  }

  const handleSessionFlagChanged = (sessionId: string, isFlagged: boolean) => {
    if (filterState.filter.flagged && !isFlagged) {
      setCustomer(prevCustomer => prevCustomer && {
        ...prevCustomer,
        sessions: prevCustomer.sessions.filter(session => session.id !== sessionId)
      })
    } else {
      setCustomer(prevCustomer => prevCustomer && {
        ...prevCustomer,
        customerFlag: prevCustomer.customerFlag || isFlagged
          ? prevCustomer.customerFlag
            ?? {
              customerId: prevCustomer.id,
              countryCode: getCountryCode(countryCode, acr),
              acr: acr,
              updatedById: '',
              updatedByEmail: '',
              createdAt: '',
              updatedAt: ''
            }
          : null,
        sessions: prevCustomer.sessions.map(session =>
          session.id === sessionId
            ? { 
              ...session,
              isSessionFlagged: isFlagged,
              isCustomerFlagged: isFlagged || session.isCustomerFlagged
            } : {
              ...session,
              isCustomerFlagged: isFlagged || session.isCustomerFlagged
            }
        )
      })
    }
  }

  const handleSortingChange = (e: SortingChangedEvent) => {
    const newOptions = e.detail
    setSortingOptions(newOptions)
  }

  return (
    <>
      <div className='mb-5'>
        <NavigateBack backUrl={backUrl.current} />
      </div>
      <div className='customer-info list-content__container'>
        <div className='list list-content__left'>
          {loading && (
            <div className='grid place-content-center'>
              <SpinnerIcon />
            </div>
          )}

          {/* Desktop */}
          {customer && !deviceState.isMobile && acr && (
            <div className='customer-info__list-item list p-4'>
              <CustomerCard customer={customer} countryCode={getCountryCode(countryCode, acr)} />
            </div>
          )}

          {/* Mobile */}
          {customer && deviceState.isMobile && acr && (
            <div className='customer-info__list-item list p-4'>
              <CustomerCard customer={customer} countryCode={getCountryCode(countryCode, acr)} />
              <hr className='mt-4' />
              <CustomerBannedCard
                customer={customer}
                onBanToggle={toggleBanCustomer}
                onBanReasonChange={handleBanReasonChange}
                onBanReasonsReset={handleBanReasonsReset}
                isSavingBanReasons={isSavingBanReasons}
                banError={banError}
              />
              <hr className='mt-4' />
              <CustomerFlaggedCard
                customer={customer}
                onFlagToggle={toggleFlagCustomer}
                flagError={flagError}
              />
              <hr className='mt-4' />
              <CustomerFilterCard />
            </div>
          )}

          <div className={`flex mt-6 ${deviceState.isMobile ? 'mb-4 items-end' : 'mb-2'}`}>
            <div className={`list-content__count flex ${deviceState.isMobile ? 'flex-col gap-1' : ''}`}>
              <span className='list-content__count-label mr-3'>Visar {customer?.sessions.length} kundvarv</span>
              <RefreshIcon size='16px' fontSize='12px' color='var(--color-accent-red)' onClick={fetchCustomerInfo} text='Uppdatera'></RefreshIcon>
            </div>
            <div className={`ml-auto ${deviceState.isMobile ? '' : 'w-[180px]'}`}>
              <IcaSortingDropDown
                options={JSON.stringify(sortingOptions)}
                position="right"
                onSortingchanged={handleSortingChange}
              />
            </div>
          </div>
          {sortedCustomerInfo && sortedCustomerInfo.sessions.map((session) => {
            return (
              <div className='list-item' key={session.id}>
                <Link to={`/customers/${id}/${getCountryCode(countryCode, acr)}/${session.id}${location.search}`} onClick={() => historyReducer.addViewedSession(session.id)}>
                  <SessionListItem
                    session={session}
                    viewed={historyReducer.hasViewedSession(session.id)}
                    pageType='customer'
                  />
                </Link>
              </div>
            )
          }
          )}
          {!loading && !error && customer && customer.sessions.length === 0 && <SearchPlaceholder backgroundColor="var(--color-white)" placeholderText="Ingen kundhistorik kunde hittas för angiven butik." />}
          {!loading && error && <p className='error text-center'>Någonting gick fel. Försök igen genom att ladda om sidan.</p>}
        </div>

        {/* Desktop */}
        {customer && !deviceState.isMobile && (
          <div className='list-content__right'>
            <div className='customer-info__list-item list px-4 pb-4 pt-3 min-h-20'>
              <CustomerBannedCard
                customer={customer}
                onBanToggle={toggleBanCustomer}
                onBanReasonChange={handleBanReasonChange}
                onBanReasonsReset={handleBanReasonsReset}
                isSavingBanReasons={isSavingBanReasons}
                banError={banError}
              />
            </div>
            <div className='customer-info__list-item list px-4 pb-4 pt-3 mt-2 min-h-20'>
              <CustomerFlaggedCard
                customer={customer}
                onFlagToggle={toggleFlagCustomer}
                flagError={flagError}
              />
            </div>
            <div className='customer-info__list-item list px-4 pb-4 pt-3 mt-2 min-h-20'>
              <CustomerFilterCard />
            </div>
          </div>
        )}
      </div>

      {sessionId &&
        <IcaDialog
          open={!!sessionId}
          onClose={onModalClose}
          snaptotop
        >
          <IcaHeader
            slot='header'
            variant='dialog'
            heading='Kundvarv'
            leftaction='close'
            onLeftactionclick={onModalClose}
          />
          <SessionInfo onSessionFlagChanged={(sessionId, isFlagged) => handleSessionFlagChanged(sessionId, isFlagged)} />
        </IcaDialog>
      }
    </>
  )
}
